import { Immunization, Patient, ValueSetExpansionContains } from '@medplum/fhirtypes'
import { ChartItem } from './PatientChart.utils.tsx'
import { Box, Select, TextInput } from '@mantine/core'
import { DateInput } from '@/components/ui/DateInput'
import { parseDateInputValue } from '@/utils/dateInput.ts'
import { ResourceTable } from '@/pages/Patient/components/ResourceTable.tsx'
import { formatDateTime } from '@/utils.ts'
import { useState } from 'react'
import { ValueSetAutocomplete } from '@medplum/react'
import { AddResourceSection } from './AddResourceSection.tsx'

type Props = {
  patient: Patient
  item: ChartItem
}

const VACCINE_VALUE_SET = 'http://cts.nlm.nih.gov/fhir/ValueSet/2.16.840.1.113883.10.20.5.9.10'

const STATUS_OPTIONS: { value: NonNullable<Immunization['status']>; label: string }[] = [
  { value: 'completed', label: 'Completed' },
  { value: 'entered-in-error', label: 'Entered in Error' },
  { value: 'not-done', label: 'Not Done' },
]

const availFields = [
  { code: 'vaccine', title: 'Vaccine' },
  { code: 'route', title: 'Route' },
  { code: 'date', title: 'Date' },
  { code: 'status', title: 'Status' },
  { code: 'dose', title: 'Dose' },
  { code: 'instruction', title: 'Instruction' },
  { code: 'doctor', title: 'Doctor' },
]

const renderColumn = (resource: Immunization, itemCode: string) => {
  switch (itemCode) {
    case 'vaccine': {
      const main = resource.vaccineCode?.text || resource.vaccineCode?.coding?.[0]?.display || ''
      const lot = resource.lotNumber ? `Lot #: ${resource.lotNumber}` : ''
      const manufacturer = resource.manufacturer?.display || resource.manufacturer?.reference || ''
      const lines = [main]
      if (lot) lines.push(lot)
      if (manufacturer) lines.push(`Manufacturer: ${manufacturer}`)
      return (
        <div>
          {lines.map((l, i) => (
            <div key={i}>{l}</div>
          ))}
        </div>
      )
    }
    case 'route':
      return resource.route?.coding?.[0]?.display || resource.route?.coding?.[0]?.code || ''
    case 'date': {
      const date = resource.occurrenceDateTime || resource.recorded
      if (!date) return ''
      return formatDateTime(date)
    }
    case 'status':
      if (
        resource.status === 'not-done' ||
        resource.extension?.some((e) => e.url.includes('notGiven') && e.valueBoolean)
      ) {
        return 'refused'
      }
      return resource.status || ''
    case 'dose':
      if (resource.doseQuantity && resource.doseQuantity.value) {
        return `${resource.doseQuantity.value} ${resource.doseQuantity.unit || ''}`.trim()
      }
      return ''
    case 'instruction':
      return ''
    case 'doctor':
      if (resource.performer?.length && resource.performer[0].actor?.display) {
        return resource.performer[0].actor.display
      }
      return ''
    default:
      return ''
  }
}

const preprocessList = (list: Immunization[]): Immunization[] => {
  const resourceList: Immunization[] = []
  for (const item of list) {
    if (
      !resourceList.some(
        (r) =>
          ((r.recorded && r.recorded === item.recorded) ||
            (r.occurrenceDateTime && r.occurrenceDateTime === item.occurrenceDateTime)) &&
          r.vaccineCode?.text &&
          r.vaccineCode?.text === item.vaccineCode?.text,
      )
    ) {
      resourceList.push(item)
    }
  }

  return resourceList
}

export const Immunizations = ({ patient, item }: Props) => {
  const [picked, setPicked] = useState<{ system?: string; code: string; display: string } | null>(
    null,
  )
  const [pickerKey, setPickerKey] = useState(0)
  const [status, setStatus] = useState<string | null>('completed')
  const [occurrenceDate, setOccurrenceDate] = useState<Date | null>(new Date())
  const [lotNumber, setLotNumber] = useState('')

  const resetForm = () => {
    setPicked(null)
    setStatus('completed')
    setOccurrenceDate(new Date())
    setLotNumber('')
    setPickerKey((k) => k + 1)
  }

  const handlePickerChange = (items: ValueSetExpansionContains[]) => {
    const first = items[0]
    if (!first?.code) {
      setPicked(null)
      return
    }
    setPicked({
      system: first.system,
      code: first.code,
      display: first.display || first.code,
    })
  }

  const buildResource = (): Immunization | { error: string } => {
    if (!picked?.code || !occurrenceDate) {
      return { error: 'Please select a vaccine and date administered.' }
    }
    return {
      resourceType: 'Immunization',
      status: (status || 'completed') as NonNullable<Immunization['status']>,
      patient: { reference: `Patient/${patient.id}` },
      vaccineCode: {
        coding: [
          {
            system: picked.system,
            code: picked.code,
            display: picked.display,
          },
        ],
        text: picked.display,
      },
      occurrenceDateTime: occurrenceDate.toISOString(),
      recorded: new Date().toISOString(),
      lotNumber: lotNumber.trim() || undefined,
    }
  }

  return (
    <AddResourceSection<Immunization>
      patient={patient}
      title="Immunizations"
      modalTitle="Add Immunization"
      resourceType="Immunization"
      featureContext="Immunization"
      noun="immunization"
      isSubmitDisabled={!picked?.code || !occurrenceDate}
      resetForm={resetForm}
      buildResource={buildResource}
      renderForm={() => (
        <>
          <Box>
            <p className="mb-1 text-sm font-medium">Vaccine *</p>
            <ValueSetAutocomplete
              key={pickerKey}
              name="add-immunization"
              placeholder="Search vaccine (CVX)"
              binding={VACCINE_VALUE_SET}
              maxValues={1}
              clearable={false}
              onChange={handlePickerChange}
            />
            {picked && (
              <p className="mt-1 text-xs text-gray-500">
                Selected: {picked.display} ({picked.code})
              </p>
            )}
          </Box>
          <DateInput
            label="Date Administered *"
            value={occurrenceDate}
            onChange={(value) => setOccurrenceDate(parseDateInputValue(value))}
            clearable
            placeholder="Pick a date"
          />
          <Select label="Status" data={STATUS_OPTIONS} value={status} onChange={setStatus} />
          <TextInput
            label="Lot Number"
            placeholder="Optional"
            value={lotNumber}
            onChange={(e) => setLotNumber(e.currentTarget.value)}
          />
        </>
      )}
    >
      {(refreshKey) => (
        <ResourceTable
          key={refreshKey}
          patient={patient}
          item={item}
          availFields={availFields}
          renderColumn={renderColumn}
          defaultSort="date"
          blockRemove
          preprocessList={preprocessList}
        />
      )}
    </AddResourceSection>
  )
}
